import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import classnames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import React, { useRef, useEffect, useCallback, useState, startTransition } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { AvailableLocales, Locale } from '@cineamo/ui/src/constants/locale-constants';

import { getLanguageCodeFromLocale, getLocationNameFromLocale } from '@cineamo/ui/src/helpers/locale-helper';
import { setPreferredLocale } from '@cineamo/ui/src/helpers/preferred-language-helper';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import { IconClose24 } from '@cineamo/ui/icons/ic-close';
import { IconGlobe24 } from '@cineamo/ui/icons/ic-globe';
import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';
import { IconFacebookSolid48 } from '@cineamo/ui/src/icons/ic-facebook';
import { IconInstagramOutline24 } from '@cineamo/ui/src/icons/ic-instagram';
import { IconShareBox24 } from '@cineamo/ui/src/icons/ic-share-box';
import { IconTiktok48 } from '@cineamo/ui/src/icons/ic-tiktok';
import { IconYoutube24 } from '@cineamo/ui/src/icons/ic-youtube';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { usePathname, useRouter } from '@/i18n/routing';

import { useHomeEntertainment } from './home-entertainment/useHomeEntertainment';

const PageMenu: React.FC<{
    isMenuOpen: boolean;
    onClose: () => void;
    onWatchTrailerClick: () => void;
    /** Opens the showtimes finder, or the home-entertainment offers when the page is in that mode. */
    onPrimaryCtaClick: () => void;
    onPlotClick: () => void;
    onInfoClick: () => void;
    onShareClick: () => void;
}> = ({ isMenuOpen, onWatchTrailerClick, onPrimaryCtaClick, onPlotClick, onInfoClick, onShareClick, onClose }) => {
    const { domainConfiguration, movie } = useDomainConfiguration();
    const { isActive: isHomeEntertainment, buttonLabel: homeEntertainmentLabel } = useHomeEntertainment();
    const { track } = useAnalytics();
    const t = useTranslations();
    const locale = useLocale();
    const router = useRouter();
    const currentPath = usePathname();
    const searchParams = useSearchParams();
    const menuRef = useRef<HTMLDivElement>(null);
    const linksScrollRef = useRef<HTMLUListElement>(null);
    const [menuHeight, setMenuHeight] = useState<number | null>(null);

    const { styleFadedState } = useScrollState({
        scrollContainerRef: linksScrollRef,
        orientation: 'vertical'
    });

    const queryParams: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
        queryParams[key] = value;
    });

    async function onLanguageChange(selectedLocale: Locale) {
        await setPreferredLocale(selectedLocale);
        const newLocale = getLanguageCodeFromLocale(selectedLocale);

        // Build the path with query params if any
        let path = currentPath;
        const queryString = new URLSearchParams(queryParams).toString();
        if (queryString) {
            path = `${currentPath}?${queryString}`;
        }

        startTransition(() => {
            router.replace(path, {
                locale: newLocale
            });
        });
    }

    useEffect(() => {
        if (menuRef.current) {
            setMenuHeight(menuRef.current.scrollHeight);
        }
    }, [isMenuOpen]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isMenuOpen) {
                onClose();
            }
        },
        [isMenuOpen, onClose]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const movieTitle = movie.title;

    // Use CSS class for initial state to avoid hydration mismatch
    const useInlineStyle = menuHeight !== null;

    return (
        <div
            ref={menuRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('common.menu')}
            aria-hidden={!isMenuOpen}
            className={classnames(
                'absolute z-10 w-full h-full flex flex-col items-center overflow-hidden',
                'bg-[var(--background-color)]',
                'transition-all duration-300',
                !useInlineStyle && (isMenuOpen ? 'top-0' : '-top-[100dvh]')
            )}
            style={
                useInlineStyle
                    ? {
                          top: isMenuOpen ? 0 : `${-Math.max(menuHeight, window.innerHeight)}px`
                      }
                    : undefined
            }>
            <div className="px-16 py-9 w-full flex flex-row justify-between flex-shrink-0">
                <button
                    className="p-12 cursor-pointer"
                    onClick={() => onClose()}
                    aria-label={t('common.close')}>
                    <IconClose24 aria-hidden="true" />
                </button>
                <button
                    className="p-12 cursor-pointer"
                    onClick={() => onShareClick()}
                    aria-label={t('common.share')}>
                    <IconShareBox24 aria-hidden="true" />
                </button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center w-full">
                <div className="flex flex-col items-center min-h-0 max-h-full">
                    {domainConfiguration.titleArtworkUrl && (
                        <div
                            className={classnames(
                                'flex w-[80vw] max-h-[150px] flex-shrink-0',
                                'h-auto',
                                domainConfiguration.titleArtworkOrientation === 'portrait'
                                    ? 'max-w-[416px]'
                                    : 'max-w-[740px]'
                            )}>
                            <Image
                                className="w-full max-h-full object-contain"
                                src={domainConfiguration.titleArtworkUrl}
                                width={10000}
                                height={10000}
                                alt={`${movieTitle}`}
                            />
                        </div>
                    )}
                    <menu className="mt-[64px] min-h-0 flex-1 w-full">
                        <nav
                            aria-label={t('common.menu')}
                            className="relative h-full">
                            <ul
                                ref={linksScrollRef}
                                className="flex flex-col space-y-20 lg:space-y-24 text-headline-20 lg:text-headline-24 leading-[24px] lg:leading-[36px] text-center overflow-y-auto max-h-full no-scrollbar py-4"
                                style={styleFaded(styleFadedState, 10)}>
                                <li>
                                    <button
                                        onClick={() => {
                                            onClose();
                                            onWatchTrailerClick();
                                        }}>
                                        {t('menu.trailer')}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => {
                                            onClose();
                                            onPrimaryCtaClick();
                                        }}>
                                        {isHomeEntertainment ? homeEntertainmentLabel : t('menu.tickets')}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => {
                                            onClose();
                                            track('Page Menu Plot Link Clicked');
                                            onPlotClick();
                                        }}>
                                        {t('menu.plot')}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        onClick={() => {
                                            onClose();
                                            onInfoClick();
                                        }}>
                                        {t('menu.info')}
                                    </button>
                                </li>
                                {domainConfiguration.competition?.[locale] && (
                                    <li>
                                        <a
                                            href="#contest"
                                            onClick={() => {
                                                onClose();
                                            }}>
                                            {t('competition.title')}
                                        </a>
                                    </li>
                                )}
                                {domainConfiguration.movieTourId && (
                                    <li>
                                        <a
                                            href="#kinotour"
                                            onClick={() => {
                                                onClose();
                                            }}>
                                            {t('cinema-tour.title')}
                                        </a>
                                    </li>
                                )}
                                {domainConfiguration.partners?.length > 0 && (
                                    <li>
                                        <a
                                            href="#partners"
                                            onClick={() => {
                                                onClose();
                                            }}>
                                            {t('partners.title')}
                                        </a>
                                    </li>
                                )}
                                {domainConfiguration.pressQuotes?.[locale]?.length > 0 && (
                                    <li>
                                        <a
                                            href="#reviews"
                                            onClick={() => {
                                                onClose();
                                            }}>
                                            {t('movie.press-quotes')}
                                        </a>
                                    </li>
                                )}
                            </ul>
                            <BackAndForthButtonOverlay
                                scrollContainerRef={linksScrollRef}
                                orientation="vertical"
                                renderOnMobile
                                buttonUnavailableStyle="hidden"
                            />
                        </nav>
                    </menu>
                </div>
            </div>
            <div className="flex flex-row w-full justify-between px-16 py-9 flex-shrink-0">
                <Popover className="relative z-50">
                    {({ open }) => (
                        <>
                            <PopoverButton className="flex flex-row items-center gap-8 outline-none cursor-pointer">
                                <IconGlobe24 /> <span className="uppercase">{locale}</span>
                            </PopoverButton>
                            {open && (
                                <PopoverPanel
                                    static
                                    className="absolute bottom-full left-0 mb-8 flex flex-col bg-white rounded-4 shadow-lg border border-white/10 min-w-[200px] text-black">
                                    {AvailableLocales.map((availableLocale) => (
                                        <button
                                            key={availableLocale}
                                            onClick={() => onLanguageChange(availableLocale)}
                                            className={classnames(
                                                'px-16 py-12 text-left hover:bg-black/10 transition-colors first:rounded-t-4 last:rounded-b-4 whitespace-nowrap',
                                                availableLocale === locale && 'bg-black/5'
                                            )}>
                                            <span className="text-body-14">
                                                {getLanguageCodeFromLocale(availableLocale).toUpperCase()} -{' '}
                                                {getLocationNameFromLocale(availableLocale)}
                                            </span>
                                        </button>
                                    ))}
                                </PopoverPanel>
                            )}
                        </>
                    )}
                </Popover>
                <div className="flex flex-row items-center gap-8">
                    {domainConfiguration.tiktokLink && (
                        <Link
                            href={domainConfiguration.tiktokLink}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="p-12"
                            aria-label="TikTok">
                            <IconTiktok48
                                className="w-24 h-24"
                                aria-hidden="true"
                            />
                        </Link>
                    )}
                    {domainConfiguration.youtubeLink && (
                        <Link
                            href={domainConfiguration.youtubeLink}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="p-12"
                            aria-label="YouTube">
                            <IconYoutube24
                                className="w-24 h-24"
                                aria-hidden="true"
                            />
                        </Link>
                    )}
                    {domainConfiguration.facebookLink && (
                        <Link
                            href={domainConfiguration.facebookLink}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="p-12"
                            aria-label="Facebook">
                            <IconFacebookSolid48
                                className="w-24 h-24"
                                aria-hidden="true"
                            />
                        </Link>
                    )}
                    {domainConfiguration.instagramLink && (
                        <Link
                            href={domainConfiguration.instagramLink}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="p-12"
                            aria-label="Instagram">
                            <IconInstagramOutline24
                                className="w-24 h-24"
                                aria-hidden="true"
                            />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PageMenu;
